import { createContext, useContext, useEffect, useRef } from "react";
import type { ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@repo/auth";
import { useRealtimeInvalidation } from "@repo/realtime";
import type { RealtimeStatus } from "@repo/realtime";
import { resolveVenueQueryKey } from "../realtime/eventQueryKeyMap";

/**
 * Exactly ONE venue-scoped SSE subscription per tab (ADR-0012), mounted by the `/v/:venueId` layout
 * and remounted on venue switch (the `key={venueId}` on the provider gives clean channel scoping).
 * Events invalidate only `["venue", venueId, …]` keys via [resolveVenueQueryKey]; the connection
 * status is exposed through [useVenueRealtime] for the header's "not live" indicator.
 *
 * Reconnect = snapshot refetch (never event replay, features/realtime-sync.md degraded mode): when
 * the stream comes back UP after having dropped, every active `["venue", venueId]` query is
 * invalidated so the tab catches up on whatever it missed while disconnected.
 */
const VenueRealtimeCtx = createContext<RealtimeStatus>("closed");

function realtimeUrl(venueId: string): string {
  // Read import.meta.env directly (same seam as @repo/realtime's default URL) so this never trips
  // the strict env schema in tests where VITE_API_URL is unset. `venue=` is the topic param on the
  // existing endpoint (codebase/tasks/realtime-channel.md); server-side channel authorization
  // hardening belongs to tasks/realtime-sync.md.
  const base: string = import.meta.env?.VITE_API_URL ?? "";
  return `${base}/api/v1/realtime/events?venue=${encodeURIComponent(venueId)}`;
}

export function VenueRealtimeProvider({
  venueId,
  children,
}: {
  venueId: string;
  children: ReactNode;
}) {
  const { getToken, isAuthenticated } = useAuth();
  const queryClient = useQueryClient();

  const status = useRealtimeInvalidation({
    url: realtimeUrl(venueId),
    getToken,
    enabled: isAuthenticated,
    resolveQueryKey: (event) => resolveVenueQueryKey(venueId, event.type),
  });

  // Invalidate on RE-connect only (not the first connect): refetch as the catch-up snapshot.
  const hasConnected = useRef(false);
  useEffect(() => {
    if (status !== "open") return;
    if (hasConnected.current) {
      void queryClient.invalidateQueries({ queryKey: ["venue", venueId] });
    }
    hasConnected.current = true;
  }, [status, venueId, queryClient]);

  return <VenueRealtimeCtx.Provider value={status}>{children}</VenueRealtimeCtx.Provider>;
}

/** The live SSE status for the venue in view — drives the "not live" indicator. */
export function useVenueRealtime(): RealtimeStatus {
  return useContext(VenueRealtimeCtx);
}
